import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import * as api from "../services";
import useSettings from "../User/useSettings";
import usePublicMoodboards from "./ImportExport/usePublicMoodboards";
import BoardsContext from "./boardsContext";
import { useMutation } from "react-query";
import useSaveSettings from "../User/useSaveSettings";
import AppContext from "../appContext";
import useAccountSettings from "../ManageAccount/useAccountSettings";
import TimeViewSelector from "./TimeViewSelector";
import { handlePrintMoodboardsToPDF } from "./printTools";

const ITEM_HEIGHT = 48;

function Title({ handleActiveBoardId }) {
  // client
  const { t } = useTranslation();
  const {
    app: {
      client: { id: clientId },
      project: { id: projectId, boardId: projectBoardId },
      sessionId,
    },
  } = useContext(AppContext);
  const { moodboardDispatch, activeBoardId, isLocalMoodboard } =
    useContext(BoardsContext);
  const [openMenu, setOpenMenu] = useState(false);
  const openMenuBool = Boolean(openMenu);
  const [timeRange, setTimeRange] = useState("custom");
  const [isLoading, setIsLoading] = useState(true);

  // server
  const { settings, isSettingsLoading } = useSettings(); // remember current board id
  const { publicMoodboards, isPublicMoodboardsLoading } = usePublicMoodboards(); // get public boards
  const { accountSettings } = useAccountSettings();
  const saveSettings = useSaveSettings();

  const localBoard = { id: 0, name: t("moodboards.charts.my") };
  const availableBoards = [...publicMoodboards, localBoard].reverse();
  const activeBoardName = isLocalMoodboard(activeBoardId)
    ? localBoard.name
    : publicMoodboards?.find((board) => board.id === activeBoardId)?.name ??
      "No name";

  const importLocalMoodboardMutation = useMutation(
    () => api.getLocalMoodboard({ clientId, projectId, sessionId }),
    {
      onMutate: () => handleActiveBoardId(localBoard.id),
      onSuccess: (data) => {
        if (data?.status > 0) {
          moodboardDispatch({
            type: "SET_MOODBOARDS",
            payload: data.moodBoard,
          });
        }
      },
    }
  );
  const importPublicMoodboardMutation = useMutation(
    (id) => api.getPublicMoodboard({ id, projectId, sessionId }),
    {
      onMutate: (id) => handleActiveBoardId(id),
      onSuccess: (data) => {
        if (data.data?.status > 0) {
          moodboardDispatch({
            type: "SET_MOODBOARDS",
            payload: data.data.moodBoard,
          });
        }
      },
    }
  );

  const editMoodboard = publicMoodboards?.find(
    (board) => board.id === settings.moodBoardId
  );

  // constructor at startup
  useEffect(() => {
    const importBoardById = (id) => {
      if (isLocalMoodboard(id)) {
        // import local moodboard
        importLocalMoodboardMutation.mutate();
      } else {
        // import public moodboard
        importPublicMoodboardMutation.mutate(id);
      }
      setIsLoading(false);
    };

    if (!isSettingsLoading && !isPublicMoodboardsLoading && isLoading) {
      if (settings.boardId !== null) {
        importBoardById(settings.boardId);
      } else {
        importBoardById(projectBoardId);
      }
    }
  }, [isSettingsLoading, isPublicMoodboardsLoading]);

  // open/close/click the menu
  const handleMenu = (event) => {
    setOpenMenu(event.currentTarget);
  };
  const handleClose = () => {
    setOpenMenu(false);
  };

  const handleBoardClick = ({ id }) => {
    moodboardDispatch({ type: "SET_MOODBOARDS", payload: null }); // reset the boards
    handleTimeRange("custom"); // reset the time range
    if (isLocalMoodboard(id)) {
      importLocalMoodboardMutation.mutate();
    } else {
      importPublicMoodboardMutation.mutate(id);
    }
    //remember the selected board view
    saveSettings.mutate({ boardId: id });
    handleClose();
  };

  const handleTimeRange = (view) => {
    setTimeRange(view);
  };

  if (availableBoards.length <= 1) return "";
  return (
    <Box display="flex" alignItems="center" paddingBottom={2} width="100%">
      <Box width={"100%"}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Button
            onClick={handleMenu}
            color="inherit"
            endIcon={<ArrowDropDownIcon />}
          >
            <Typography variant="h6">{activeBoardName}</Typography>
          </Button>
          <Box display={"flex"} alignItems="center" style={{ gap: 8 }}>
            {editMoodboard &&
            isLocalMoodboard(activeBoardId) &&
            accountSettings.moodBoardLibrarySave ? (
              <Typography>Editing "{editMoodboard.name}"</Typography>
            ) : null}
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={handlePrintMoodboardsToPDF}
              color="inherit"
            >
              <Typography variant="subtitle2" color="textSecondary">
                {t("moodboards.settings.printToPDF")}
              </Typography>
            </Button>
            <TimeViewSelector
              selected={timeRange}
              handleSelected={handleTimeRange}
            />
          </Box>
        </Box>
        <Divider />
      </Box>
      <Menu
        anchorEl={openMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
        open={openMenuBool}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 8.5,
          },
        }}
      >
        <Typography
          sx={{ padding: 1 }}
          variant="subtitle2"
          color="textSecondary"
        >
          {t("moodboards.placeholders.availableGraphs")}
        </Typography>
        {availableBoards?.map((board) => (
          <MenuItem
            key={board.id}
            onClick={() => handleBoardClick(board)}
            selected={activeBoardId === board.id}
          >
            {board.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default Title;
